import {
  Box,
  Flex,
  Icon,
  Modal as ChakraModal,
  ModalContent,
  ModalOverlay,
  Text,
  Tooltip
} from '@chakra-ui/react';
import {
  faCircleCheck,
  faDownload,
  faInfoCircle,
  faUserHeadset
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, ButtonIcon, ButtonText } from '@morressier/alembic';
import { useRef } from 'react';
import {
  ISubmissionFile,
  ISubmissionFileSet
} from '@publishing-engine/domain/models';
import { PdfViewerContainer } from 'src/components/PdfViewer/PdfViewerContainer';
import { downloadFile } from 'src/utils/file';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  fileSet: Pick<
    ISubmissionFileSet,
    'ConsolidatedPdf' | 'AnonymizedConsolidatedPdf'
  >;
  submissionContextId: string;
  organizationId?: string;
  isAcknowledged?: boolean;
  onAcknowledge?: () => void;
  onContactSupport?: () => void;
  isContactSupportOpen?: boolean;
}

const ConsolidatedPdfPreviewModal: React.FC<Props> = ({
  isOpen,
  onClose,
  fileSet,
  submissionContextId,
  organizationId,
  isAcknowledged = true,
  onAcknowledge,
  onContactSupport,
  isContactSupportOpen = false
}) => {
  const previewCloseButtonRef = useRef<HTMLButtonElement>(null);

  const handleDownloadPdf = (file?: ISubmissionFile) => {
    if (file) {
      void downloadFile(file.url, { submissionContextId, organizationId });
    }
  };

  return (
    <ChakraModal
      isOpen={isOpen}
      onClose={() => {
        if (!isContactSupportOpen) onClose();
      }}
      closeOnEsc={!isContactSupportOpen}
      closeOnOverlayClick={!isContactSupportOpen}
      initialFocusRef={previewCloseButtonRef}
      size="full"
      isCentered
    >
      <ModalOverlay />
      <ModalContent
        width="100vw"
        height="100vh"
        maxWidth="100vw"
        maxHeight="100vh"
        margin={0}
        borderRadius={0}
        overflow="hidden"
        position="relative"
        background="surfaceSecondaryFill"
      >
        <Flex
          height="100%"
          minHeight={0}
          paddingX={4}
          flexDirection="column"
          gap={2}
        >
          <Box>
            <Box as="button" />
            <Flex justifyContent="space-between" alignItems="center">
              <Text textStyle="headingGroup">
                Consolidated Submission File Preview
                <Tooltip
                  hasArrow
                  shouldWrapChildren
                  placement="top"
                  label={
                    'If you find any issues in the preview, please replace the uploaded file in the submission flow - the preview will be updated on the next generation.'
                  }
                  borderRadius={4}
                >
                  <ButtonIcon
                    size="small"
                    ml={1}
                    ariaLabel="Help Icon"
                    icon={<FontAwesomeIcon icon={faInfoCircle} />}
                  />
                </Tooltip>
              </Text>
              {onContactSupport && (
                <ButtonText
                  leftIcon={<Icon as={FontAwesomeIcon} icon={faUserHeadset} />}
                  onClick={onContactSupport}
                  mr={3}
                >
                  Contact Support
                </ButtonText>
              )}
            </Flex>
          </Box>

          <Flex
            flex={1}
            minHeight={0}
            gap={3}
            alignItems="stretch"
            flexDirection={{ base: 'column', md: 'row' }}
          >
            <Flex
              flex={1}
              minWidth={0}
              minHeight={0}
              flexDirection="column"
              gap={2}
            >
              <Flex justifyContent="space-between" alignItems="center">
                <Text textStyle="headingItem">
                  Editorial View{' '}
                  <Tooltip
                    hasArrow
                    shouldWrapChildren
                    placement="top"
                    label={
                      'This version is for editors and administrators. It includes author names, affiliations, and other identifying details. Review it to confirm all information is complete and accurate.'
                    }
                    borderRadius={4}
                  >
                    <ButtonIcon
                      size="small"
                      ml={1}
                      ariaLabel="Help Icon"
                      icon={<FontAwesomeIcon icon={faInfoCircle} />}
                    />
                  </Tooltip>
                </Text>
                <Button
                  variant="secondary"
                  rightIcon={<FontAwesomeIcon icon={faDownload} />}
                  onClick={() => handleDownloadPdf(fileSet.ConsolidatedPdf)}
                >
                  Download Editorial PDF
                </Button>
              </Flex>
              {fileSet.ConsolidatedPdf ? (
                <PdfViewerContainer
                  fileUrl={fileSet.ConsolidatedPdf.url}
                  fileExtension={fileSet.ConsolidatedPdf.extension}
                  submissionContextId={submissionContextId}
                  organizationId={organizationId}
                  viewerHeight="max(120px, calc(100vh - 245px))"
                />
              ) : (
                <Text textStyle="textContext">Editorial PDF unavailable.</Text>
              )}
            </Flex>
            <Flex
              flex={1}
              minWidth={0}
              minHeight={0}
              flexDirection="column"
              gap={2}
            >
              <Flex justifyContent="space-between" alignItems="center">
                <Text textStyle="headingItem">
                  Anonymous View{' '}
                  <Tooltip
                    hasArrow
                    shouldWrapChildren
                    placement="top"
                    label={
                      'This version is shared with reviewers. Author names, affiliations, and other identifying information are removed to support blind peer review. Check that nothing identifying remains before you acknowledge.'
                    }
                    borderRadius={4}
                  >
                    <ButtonIcon
                      size="small"
                      ml={1}
                      ariaLabel="Help Icon"
                      icon={<FontAwesomeIcon icon={faInfoCircle} />}
                    />
                  </Tooltip>
                </Text>
                <Button
                  variant="secondary"
                  rightIcon={<FontAwesomeIcon icon={faDownload} />}
                  onClick={() => handleDownloadPdf(fileSet.AnonymizedConsolidatedPdf)}
                >
                  Download Anonymous PDF
                </Button>
              </Flex>
              {fileSet.AnonymizedConsolidatedPdf ? (
                <PdfViewerContainer
                  fileUrl={fileSet.AnonymizedConsolidatedPdf.url}
                  fileExtension={fileSet.AnonymizedConsolidatedPdf.extension}
                  submissionContextId={submissionContextId}
                  organizationId={organizationId}
                  viewerHeight="max(120px, calc(100vh - 245px))"
                />
              ) : (
                <Text textStyle="textContext">Anonymous PDF unavailable.</Text>
              )}
            </Flex>
          </Flex>
          <Flex
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            justifyContent="space-between"
            alignItems="center"
            gap={3}
            flexWrap="wrap"
            flexShrink={0}
            paddingY={3}
            background="white"
            borderTopWidth="1px"
            borderTopColor="gray.200"
            zIndex={1}
            paddingX={4}
          >
            <Button
              ref={previewCloseButtonRef}
              variant="caution"
              onClick={onClose}
            >
              Exit
            </Button>
            <Flex gap={2} alignItems="center">
              <Text textStyle="textContext">
                {!isAcknowledged
                  ? 'Please review and acknowledge each variation'
                  : 'Review the editorial and anonymous PDF previews.'}
              </Text>
              {!isAcknowledged && onAcknowledge && (
                <Button
                  variant="primary"
                  rightIcon={<FontAwesomeIcon icon={faCircleCheck} />}
                  onClick={onAcknowledge}
                >
                  Acknowledge Consolidated PDFs
                </Button>
              )}
            </Flex>
          </Flex>
        </Flex>
      </ModalContent>
    </ChakraModal>
  );
};

export default ConsolidatedPdfPreviewModal;
